/**
 * Página de Registro
 * Mobile-first: CTAs en thumb zone, touch targets 48px+
 * Sprint 5: Redirige a verificación de cuenta después del registro
 */

import { useState, useCallback } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SocialAuthButton, type SocialProvider } from '../../components/auth/SocialAuthButton';
import { useAuthStore } from '../../stores';
import { Input, Button } from '../../components/ui';
import { savePendingEmail } from '../../services/otp';
import type { ApiError } from '../../types';

type RegistrationStep = 'method' | 'details';

function RegisterSuccess({ email, onGoToVerify }: { email: string; onGoToVerify: () => void }) {
    return (
        <div className="animate-fade-in flex flex-col items-center py-8 text-center">
            <div className="mb-5 flex size-16 items-center justify-center rounded-2xl bg-success/15 text-success">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </div>
            <h2 className="mb-2 text-xl font-bold text-text">¡Registro exitoso!</h2>
            <p className="mb-6 text-sm text-text-muted">
                Enviamos un código de verificación a <strong className="text-text-secondary">{email}</strong>
            </p>
            <button
                type="button"
                className="w-full rounded-xl bg-primary py-3.5 text-sm font-semibold text-white transition active:bg-primary/80"
                onClick={onGoToVerify}
            >
                Verificar cuenta
            </button>
        </div>
    );
}

export function RegisterPage() {
    const navigate = useNavigate();
    const { register, isLoading, error, clearError } = useAuthStore();

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [registrationStep, setRegistrationStep] = useState<RegistrationStep>('method');
    const [socialAuthMessage, setSocialAuthMessage] = useState<string | null>(null);

    // Sprint 4: Estado de registro exitoso
    const [registrationComplete, setRegistrationComplete] = useState(false);
    const [registeredEmail, setRegisteredEmail] = useState('');

    const handleEmailContinue = useCallback((event: FormEvent) => {
        event.preventDefault();
        clearError();

        const normalizedEmail = email.trim();
        if (!normalizedEmail) {
            setFieldErrors({ email: 'El correo electrónico es obligatorio' });
            return;
        }

        if (!/\S+@\S+\.\S+/.test(normalizedEmail)) {
            setFieldErrors({ email: 'Ingresa un correo electrónico válido' });
            return;
        }

        setEmail(normalizedEmail);
        setFieldErrors({});
        setRegistrationStep('details');
    }, [clearError, email]);

    const handleBackToMethod = useCallback(() => {
        clearError();
        setFieldErrors({});
        setSocialAuthMessage(null);
        setRegistrationStep('method');
    }, [clearError]);

    const handleSocialRegistration = useCallback((provider: SocialProvider) => {
        const providerName = provider === 'google' ? 'Google' : 'Facebook';
        setSocialAuthMessage(`El registro con ${providerName} estará disponible cuando terminemos de configurar la conexión segura.`);
    }, []);

    // Validación local
    const validateForm = useCallback((): boolean => {
        const errors: Record<string, string> = {};

        if (!name.trim()) {
            errors.name = 'El nombre es obligatorio';
        } else if (name.length > 255) {
            errors.name = 'El nombre no puede tener más de 255 caracteres';
        }

        if (!email.trim()) {
            errors.email = 'El correo electrónico es obligatorio';
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            errors.email = 'Ingresa un correo electrónico válido';
        }

        if (!password) {
            errors.password = 'La contraseña es obligatoria';
        } else if (password.length < 8) {
            errors.password = 'La contraseña debe tener al menos 8 caracteres';
        }

        if (!passwordConfirmation) {
            errors.password_confirmation = 'Confirma tu contraseña';
        } else if (password !== passwordConfirmation) {
            errors.password_confirmation = 'Las contraseñas no coinciden';
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    }, [name, email, password, passwordConfirmation]);

    // Submit handler
    const handleSubmit = useCallback(async (e: FormEvent) => {
        e.preventDefault();
        clearError();

        if (!validateForm()) return;

        const success = await register({
            name,
            email,
            password,
            password_confirmation: passwordConfirmation,
        });

        if (success) {
            // Sprint 5: Guardar email y redirigir a verificación
            savePendingEmail(email);
            setRegisteredEmail(email);
            setRegistrationComplete(true);
        } else {
            // Mapear errores del servidor a campos
            const serverError = useAuthStore.getState().error as ApiError | null;
            if (serverError?.errors) {
                setFieldErrors(
                    Object.fromEntries(
                        Object.entries(serverError.errors).map(([key, msgs]) => [key, msgs[0]])
                    )
                );
            }
        }
    }, [name, email, password, passwordConfirmation, register, validateForm, clearError]);

    // Handler para ir a verificación después del registro exitoso (Sprint 5)
    const handleGoToVerify = useCallback(() => {
        navigate('/verify-account', { state: { email: registeredEmail } });
    }, [navigate, registeredEmail]);

    if (registrationComplete) {
        return (
            <div className="flex min-h-dvh flex-col items-center justify-center bg-background px-6" style={{ padding: 'var(--spacing-safe-area)' }}>
                <div className="w-full max-w-[400px] px-8">
                    <RegisterSuccess email={registeredEmail} onGoToVerify={handleGoToVerify} />
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-dvh flex-col items-center bg-background px-6 pt-12" style={{ padding: 'var(--spacing-safe-area)' }}>
            <div className="w-full max-w-[400px] px-8">
                {/* Logo */}
                <div className="mb-8 flex flex-col items-center gap-3">
                    <img src="/logo3png.png" alt="Troquero" width="64" height="64" className="size-16 object-contain" />
                    <h1 className="text-xl font-bold text-text">Crear cuenta</h1>
                    <p className="text-sm text-text-muted">
                        {registrationStep === 'method' ? 'Elige cómo quieres registrarte' : 'Completa tus datos para continuar'}
                    </p>
                </div>

                {error && !error.errors && (
                    <div className="mb-4 rounded-xl border border-error/30 bg-error/8 p-3 text-xs text-error">
                        {error.message}
                    </div>
                )}

                {registrationStep === 'method' ? (
                    <div className="flex flex-col gap-5">
                        <form onSubmit={handleEmailContinue} className="flex flex-col gap-4" noValidate>
                            <Input
                                label="Correo electrónico"
                                type="email"
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                                error={fieldErrors.email}
                                placeholder="tu@email.com"
                                autoComplete="email"
                                autoCapitalize="none"
                                inputMode="email"
                            />
                            <Button type="submit" variant="primary" size="large" fullWidth>
                                Continuar con correo
                            </Button>
                        </form>

                        <div className="flex items-center gap-3" aria-hidden="true">
                            <span className="h-px flex-1 bg-border" />
                            <span className="text-sm text-text-muted">o</span>
                            <span className="h-px flex-1 bg-border" />
                        </div>

                        <div className="flex flex-col gap-3">
                            <SocialAuthButton provider="google" onClick={() => handleSocialRegistration('google')} />
                            <SocialAuthButton provider="facebook" onClick={() => handleSocialRegistration('facebook')} />
                        </div>

                        {socialAuthMessage && (
                            <p className="text-center text-xs text-text-muted" role="status">{socialAuthMessage}</p>
                        )}

                        <p className="text-center text-sm text-text-muted">
                            ¿Ya tienes cuenta?{' '}
                            <Link to="/login" className="font-medium text-primary">Inicia sesión</Link>
                        </p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
                        <button
                            type="button"
                            onClick={handleBackToMethod}
                            className="self-start text-sm font-medium text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35"
                        >
                            ← Cambiar método de registro
                        </button>
                        <Input
                            label="Nombre"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            error={fieldErrors.name}
                            placeholder="Tu nombre completo"
                            autoComplete="name"
                            disabled={isLoading}
                        />
                        <Input
                            label="Correo electrónico"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            error={fieldErrors.email}
                            placeholder="tu@email.com"
                            autoComplete="email"
                            autoCapitalize="none"
                            inputMode="email"
                            disabled={isLoading}
                        />
                        <Input
                            label="Contraseña"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            error={fieldErrors.password}
                            placeholder="Mínimo 8 caracteres"
                            autoComplete="new-password"
                            disabled={isLoading}
                        />
                        <Input
                            label="Confirmar contraseña"
                            type="password"
                            value={passwordConfirmation}
                            onChange={(e) => setPasswordConfirmation(e.target.value)}
                            error={fieldErrors.password_confirmation}
                            placeholder="Repite tu contraseña"
                            autoComplete="new-password"
                            disabled={isLoading}
                        />

                        <div className="flex flex-col gap-4 pt-4">
                            <Button type="submit" variant="primary" size="large" fullWidth isLoading={isLoading}>
                                Crear cuenta
                            </Button>
                            <p className="text-center text-sm text-text-muted">
                                ¿Ya tienes cuenta?{' '}
                                <Link to="/login" className="font-medium text-primary">Inicia sesión</Link>
                            </p>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
